import React from 'react';
import moment from 'moment';
import { CustomerConfig } from 'types';
import { isValidDate } from 'utils/dateValidation';

export type OfficeExtensionConfigWithPreventFields = CustomerConfig['office_extension_config'] & {
  onsend_guidance?: string | null;
};

export type O365PreventConfigFields = Pick<
  CustomerConfig,
  'allow_realtime_translation' | 'allow_realtime_llms' | 'allow_realtime_drafts'
> & {
  o365_policy_text?: string | null;
  o365_header_text?: string | null;
  allowed_translations?: string[];
  translation_confidence_threshold?: number;
  office_extension_config?: OfficeExtensionConfigWithPreventFields;
};

export type O365InsightConfigFields = {
  enable_outlook_scrape?: boolean;
  enable_outlook_big_backfill_scrape?: boolean;
  max_user_batch_size?: number;
  max_concurrent_outlook_scrape?: number;
  insight_folders?: string[];
  nested_insight_folders?: string[];
  outlook_scrape_all_folders_for_sent?: boolean;
  tag_incoming_mails?: boolean;
  only_custodian_senders?: boolean;
  use_internet_id_as_platform_guid?: boolean;
  enable_subscriptions?: boolean;
  enable_teams_chat_scrape?: boolean;
  enable_teams_channel_scrape?: boolean;
};

export type O365TeamsConfigFields = {
  enable_teams_chat_scrape?: boolean;
  enable_teams_channel_scrape?: boolean;
  enable_subscriptions?: boolean;
  enable_per_user_chat_subscriptions?: boolean;
  enabled_subscription_resources?: string[];
  teams_subscription_model?: string;
  teams_subscription_events?: string;
  teams_thread_context_depth?: number;
  teams_thread_context_window_hours?: number;
  fetch_thread_context_on_message?: boolean;
  teams_allow_process_attachments?: boolean;
  teams_security_group_ids?: string[];
  teams_only_allow_user_ids?: string[];
  max_user_batch_size?: number;
  max_concurrent_teams_scrape?: number;
  max_concurrent_chats_per_user_scrape?: number;
  big_backfill_teams_chats_scrape_enabled?: boolean;
  big_backfill_teams_chats_substrate_scrape_enabled?: boolean;
  skip_cache_teams_chats_scrape?: boolean;
  use_primary_credentials_for_teams_scrape?: boolean;
};

export type CustomerConfigWithPreventFields = CustomerConfig & O365PreventConfigFields;

export type CustomerConfigWithInsightFields = CustomerConfig & {
  sync_end_date?: string | null;
  o365_config: CustomerConfig['o365_config'] & O365InsightConfigFields;
};

export type CustomerConfigWithTeamsFields = CustomerConfig & {
  sync_end_date?: string | null;
  o365_config: CustomerConfig['o365_config'] & O365TeamsConfigFields;
};

export const parsePositiveInt = (value: string): number | null => {
  const parsed = Number.parseInt(value, 10);
  if (Number.isNaN(parsed) || parsed < 1) {
    return null;
  }
  return parsed;
};

export const parseNonNegativeInt = (value: string): number | null => {
  const parsed = Number.parseInt(value, 10);
  if (Number.isNaN(parsed) || parsed < 0) {
    return null;
  }
  return parsed;
};

export const parseCommaSeparatedList = (value: string): string[] =>
  value
    .split(',')
    .map((item) => item.trim())
    .filter(Boolean);

export const formatCommaSeparatedList = (value: string[] | undefined): string =>
  value?.length ? value.join(', ') : '';

export const TEAMS_CHAT_SUBSCRIPTION_RESOURCE = 'chats';
export const TEAMS_CHANNEL_SUBSCRIPTION_RESOURCE = 'teams';

export const DEFAULT_TEAMS_SUBSCRIPTION_RESOURCES = [
  TEAMS_CHAT_SUBSCRIPTION_RESOURCE,
  TEAMS_CHANNEL_SUBSCRIPTION_RESOURCE,
];

export const setSubscriptionResourceEnabled = (
  resources: string[],
  resource: string,
  enabled: boolean
): string[] => {
  const withoutResource = resources.filter((item) => item !== resource);
  if (!enabled) {
    return withoutResource;
  }

  const teamsResources = [
    TEAMS_CHAT_SUBSCRIPTION_RESOURCE,
    TEAMS_CHANNEL_SUBSCRIPTION_RESOURCE,
  ].filter(
    (teamsResource) => teamsResource === resource || withoutResource.includes(teamsResource)
  );
  const otherResources = withoutResource.filter(
    (item) =>
      item !== TEAMS_CHAT_SUBSCRIPTION_RESOURCE && item !== TEAMS_CHANNEL_SUBSCRIPTION_RESOURCE
  );

  return [...otherResources, ...teamsResources];
};

export const getTeamsSubscriptionResources = (resources: string[]): string[] =>
  [TEAMS_CHAT_SUBSCRIPTION_RESOURCE, TEAMS_CHANNEL_SUBSCRIPTION_RESOURCE].filter((resource) =>
    resources.includes(resource)
  );

export const haveSameTeamsSubscriptionResources = (left: string[], right: string[]): boolean =>
  JSON.stringify(getTeamsSubscriptionResources(left)) ===
  JSON.stringify(getTeamsSubscriptionResources(right));

export const isAnyO365ScrapeEnabled = ({
  enableOutlookScrape,
  enableTeamsChatScrape,
  enableTeamsChannelScrape,
}: {
  enableOutlookScrape: boolean;
  enableTeamsChatScrape: boolean;
  enableTeamsChannelScrape: boolean;
}): boolean => enableOutlookScrape || enableTeamsChatScrape || enableTeamsChannelScrape;

export const parseAllowedTranslations = (value: string): string[] =>
  value
    .split(',')
    .map((item) => item.trim())
    .filter(Boolean);

export const normalizeAllowedTranslations = (value: string[] | undefined): string[] =>
  value?.length ? value : ['*'];

export const formatAllowedTranslations = (value: string[] | undefined): string =>
  normalizeAllowedTranslations(value).join(', ');

export const normalizeNullableDate = (value: string): string | null => value.trim() || null;

export const utcStringToCalendarDate = (value: string): Date | undefined => {
  if (!value || !isValidDate(value)) {
    return undefined;
  }

  const utc = moment.utc(value);
  return new Date(utc.year(), utc.month(), utc.date());
};

export const calendarDateToUtcString = (date: Date): string =>
  moment.utc([date.getFullYear(), date.getMonth(), date.getDate()]).format();

type SettingsCardHeaderProps = {
  title: string;
  description: string;
  saveTestId: string;
  hasChanges: boolean;
  canCancel?: boolean;
  isValid: boolean;
  onCancel: () => void;
  onSave: () => void;
};

export const SettingsCardHeader: React.FC<SettingsCardHeaderProps> = ({
  title,
  description,
  saveTestId,
  hasChanges,
  canCancel = hasChanges,
  isValid,
  onCancel,
  onSave,
}) => (
  <div className="px-5 py-4 flex justify-between items-center">
    <div>
      <p className="text text--large text--bold">{title}</p>
      <p className="text-xs">{description}</p>
    </div>
    <div className="flex flex-row gap-2">
      <button
        type="button"
        className="button button--secondary flex justify-center w-20 h-8"
        onClick={onCancel}
        disabled={!canCancel}
      >
        <span className="font-bold">Cancel</span>
      </button>
      <button
        data-testid={saveTestId}
        type="button"
        className="button button--primary flex justify-center w-20 h-8"
        onClick={onSave}
        disabled={!hasChanges || !isValid}
      >
        <span>Save</span>
      </button>
    </div>
  </div>
);

export const SettingRow: React.FC<{
  label: string;
  description: string;
  control: React.ReactNode;
  testId?: string;
}> = ({ label, description, control, testId }) => (
  <div className="px-5 py-4 border-t border-litlingo-gray-2" data-testid={testId}>
    <div className="flex justify-between items-start gap-4">
      <div className="max-w-md">
        <p className="text text--medium">{label}</p>
        <p className="text-xs">{description}</p>
      </div>
      <div className="flex-shrink-0">{control}</div>
    </div>
  </div>
);

export const TextInputRow: React.FC<{
  id: string;
  label: string;
  description: string;
  value: string;
  onChange: (value: string) => void;
  testId: string;
  placeholder?: string;
}> = ({ id, label, description, value, onChange, testId, placeholder }) => (
  <div className="px-5 py-4 border-t border-litlingo-gray-2">
    <label className="block" htmlFor={id}>
      <p className="text text--medium">{label}</p>
      <p className="text-xs mb-2">{description}</p>
      <input
        id={id}
        data-testid={testId}
        type="text"
        value={value}
        onChange={(event): void => onChange(event.target.value)}
        className="form-input block w-full transition duration-150 ease-in-out sm:text-sm sm:leading-5"
        placeholder={placeholder}
      />
    </label>
  </div>
);

export const NumberInputRow: React.FC<{
  id: string;
  label: string;
  description: string;
  value: string;
  onChange: (value: string) => void;
  testId: string;
  min?: number;
}> = ({ id, label, description, value, onChange, testId, min = 1 }) => (
  <div className="px-5 py-4 border-t border-litlingo-gray-2">
    <label className="block" htmlFor={id}>
      <p className="text text--medium">{label}</p>
      <p className="text-xs mb-2">{description}</p>
      <input
        id={id}
        data-testid={testId}
        type="number"
        min={min}
        step={1}
        value={value}
        onChange={(event): void => onChange(event.target.value)}
        className="form-input block w-32 transition duration-150 ease-in-out sm:text-sm sm:leading-5"
      />
    </label>
  </div>
);
